import { useReducer, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { useAuth } from '../providers/AuthProvider';
import { EntryForm } from '../components/journal/EntryForm';
import { validateJournalEntry } from '../utils/journalValidation';
import { createJournalEntry } from '../graphql/journal';
import { listRecipes } from '../graphql/recipes';
import type { JournalEntryFormData, JournalValidationError } from '../types/journal';
import type { Recipe } from '../types/recipe';

const client = generateClient();

// --- State and Reducer ---

interface JournalFormState {
  formData: JournalEntryFormData;
  validationErrors: JournalValidationError[];
  isSubmitting: boolean;
  submitError: string | null;
  showRecipeSelector: boolean;
  recipes: Recipe[] | null;
  recipesLoading: boolean;
  recipesError: string | null;
}

type JournalFormAction =
  | { type: 'SET_FIELD'; field: keyof JournalEntryFormData; value: any }
  | { type: 'SET_EFFECTS'; effects: string[] }
  | { type: 'SET_RATING'; rating: number | null }
  | { type: 'SET_VALIDATION_ERRORS'; errors: JournalValidationError[] }
  | { type: 'SET_SUBMITTING'; isSubmitting: boolean }
  | { type: 'SET_SUBMIT_ERROR'; error: string | null }
  | { type: 'TOGGLE_RECIPE_SELECTOR' }
  | { type: 'CLOSE_RECIPE_SELECTOR' }
  | { type: 'SET_RECIPES_LOADING' }
  | { type: 'SET_RECIPES'; recipes: Recipe[] }
  | { type: 'SET_RECIPES_ERROR'; error: string }
  | { type: 'SELECT_RECIPE'; recipe: Recipe };

function getCurrentDatetimeLocal(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function createInitialState(): JournalFormState {
  return {
    formData: {
      servingSize: '',
      cannabinoidMg: '',
      cannabinoidType: '',
      consumedAt: getCurrentDatetimeLocal(),
      recipeId: null,
      recipeName: null,
      onsetMinutes: null,
      peakMinutes: null,
      durationMinutes: null,
      effects: [],
      notes: '',
      rating: null,
    },
    validationErrors: [],
    isSubmitting: false,
    submitError: null,
    showRecipeSelector: false,
    recipes: null,
    recipesLoading: false,
    recipesError: null,
  };
}

function journalFormReducer(state: JournalFormState, action: JournalFormAction): JournalFormState {
  switch (action.type) {
    case 'SET_FIELD':
      return {
        ...state,
        formData: { ...state.formData, [action.field]: action.value },
      };
    case 'SET_EFFECTS':
      return {
        ...state,
        formData: { ...state.formData, effects: action.effects },
      };
    case 'SET_RATING':
      return {
        ...state,
        formData: { ...state.formData, rating: action.rating },
      };
    case 'SET_VALIDATION_ERRORS':
      return { ...state, validationErrors: action.errors };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.isSubmitting };
    case 'SET_SUBMIT_ERROR':
      return { ...state, submitError: action.error };
    case 'TOGGLE_RECIPE_SELECTOR':
      return { ...state, showRecipeSelector: !state.showRecipeSelector };
    case 'CLOSE_RECIPE_SELECTOR':
      return { ...state, showRecipeSelector: false };
    case 'SET_RECIPES_LOADING':
      return { ...state, recipesLoading: true, recipesError: null };
    case 'SET_RECIPES':
      return { ...state, recipes: action.recipes, recipesLoading: false };
    case 'SET_RECIPES_ERROR':
      return { ...state, recipesError: action.error, recipesLoading: false };
    case 'SELECT_RECIPE': {
      const recipe = action.recipe;
      // Find primary cannabinoid type from first infused ingredient
      const infusedIngredient = recipe.ingredients.find((ing) => ing.isCannabisInfused);
      const cannabinoidType = infusedIngredient?.cannabinoidType || '';
      const cannabinoidMg = recipe.perServingMg != null ? String(recipe.perServingMg) : '';

      return {
        ...state,
        formData: {
          ...state.formData,
          recipeId: recipe.recipeId,
          recipeName: recipe.title,
          cannabinoidType,
          cannabinoidMg,
        },
        showRecipeSelector: false,
      };
    }
    default:
      return state;
  }
}

// --- Component ---

export function JournalEntryFormPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const [state, dispatch] = useReducer(journalFormReducer, undefined, createInitialState);

  // Move focus to heading on mount (accessibility - Req 7.6)
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // Fetch recipes when recipe selector is toggled open
  const fetchRecipes = useCallback(async () => {
    if (!user) return;
    dispatch({ type: 'SET_RECIPES_LOADING' });
    try {
      const response = await client.graphql({
        query: listRecipes,
        variables: { userId: user.userId },
      });
      const recipes = (response as { data: { listRecipes: Recipe[] } }).data.listRecipes;
      dispatch({ type: 'SET_RECIPES', recipes: recipes || [] });
    } catch {
      dispatch({ type: 'SET_RECIPES_ERROR', error: 'Could not load recipes' });
    }
  }, [user]);

  function handleToggleRecipeSelector() {
    if (!state.showRecipeSelector && state.recipes === null) {
      fetchRecipes();
    }
    dispatch({ type: 'TOGGLE_RECIPE_SELECTOR' });
  }

  function handleRecipeSelect(recipe: Recipe) {
    dispatch({ type: 'SELECT_RECIPE', recipe });
  }

  function handleRecipeSelectorClose() {
    dispatch({ type: 'CLOSE_RECIPE_SELECTOR' });
  }

  function handleFieldChange(field: keyof JournalEntryFormData, value: any) {
    dispatch({ type: 'SET_FIELD', field, value });
  }

  function handleEffectsChange(effects: string[]) {
    dispatch({ type: 'SET_EFFECTS', effects });
  }

  function handleRatingChange(rating: number | null) {
    dispatch({ type: 'SET_RATING', rating });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    dispatch({ type: 'SET_SUBMIT_ERROR', error: null });

    // 1. Validate
    const errors = validateJournalEntry(state.formData);
    dispatch({ type: 'SET_VALIDATION_ERRORS', errors });

    if (errors.length > 0) {
      return;
    }

    // 2. Generate entry ID and set createdAt
    const entryId = crypto.randomUUID();
    const createdAt = new Date().toISOString();

    // 3. Build mutation input
    const { formData } = state;
    const input = {
      userId: user!.userId,
      entryId,
      servingSize: Number(formData.servingSize),
      cannabinoidMg: Number(formData.cannabinoidMg),
      cannabinoidType: formData.cannabinoidType,
      consumedAt: new Date(formData.consumedAt).toISOString(),
      recipeId: formData.recipeId || undefined,
      recipeName: formData.recipeName || undefined,
      onsetMinutes: formData.onsetMinutes != null && formData.onsetMinutes !== '' ? Number(formData.onsetMinutes) : undefined,
      peakMinutes: formData.peakMinutes != null && formData.peakMinutes !== '' ? Number(formData.peakMinutes) : undefined,
      durationMinutes: formData.durationMinutes != null && formData.durationMinutes !== '' ? Number(formData.durationMinutes) : undefined,
      effects: formData.effects.length > 0 ? formData.effects : undefined,
      notes: formData.notes.trim() || undefined,
      rating: formData.rating ?? undefined,
      createdAt,
    };

    // 4. Submit
    dispatch({ type: 'SET_SUBMITTING', isSubmitting: true });

    try {
      await client.graphql({
        query: createJournalEntry,
        variables: { input },
      });
      navigate('/journal');
    } catch {
      dispatch({ type: 'SET_SUBMIT_ERROR', error: 'Failed to save journal entry. Please try again.' });
    } finally {
      dispatch({ type: 'SET_SUBMITTING', isSubmitting: false });
    }
  }

  return (
    <div className="page journal-entry-form-page">
      <h2 ref={headingRef} tabIndex={-1}>
        Log Entry
      </h2>

      <EntryForm
        formData={state.formData}
        validationErrors={state.validationErrors}
        isSubmitting={state.isSubmitting}
        submitError={state.submitError}
        showRecipeSelector={state.showRecipeSelector}
        recipes={state.recipes}
        recipesLoading={state.recipesLoading}
        recipesError={state.recipesError}
        onFieldChange={handleFieldChange}
        onEffectsChange={handleEffectsChange}
        onRatingChange={handleRatingChange}
        onToggleRecipeSelector={handleToggleRecipeSelector}
        onRecipeSelect={handleRecipeSelect}
        onRecipeSelectorClose={handleRecipeSelectorClose}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
